import { useState, useEffect } from 'react'
import { routeHeader } from '@/utils/routeHeader'
import { cabecalhoEfetivo, useTituloDaTelaStore } from '@/store/tituloDaTela'
import { Box, Drawer } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { Outlet, useLocation } from 'react-router-dom'

import { TerminaisPinados } from '@/components/ssm/TerminaisPinados'
import { Sidebar, useSidebarCollapsed } from './Sidebar'
import { Header } from './Header'
import { PreviewBanner } from './PreviewBanner'
import { AcessoServidoresBanner } from './AcessoServidoresBanner'
import { AppFooter } from './AppFooter'
import { PageHeader } from '@/components/ui/PageHeader'
import { ScreenErrorBoundary } from '@/components/feedback/ScreenErrorBoundary'
import { ReportScreenButton } from '@/components/feedback/ReportScreenButton'
import { ReportScreenModal } from '@/components/feedback/ReportScreenModal'
import { useInAppAlertToasts } from '@/hooks/useInAppAlertToasts'
import { useNotificationSound } from '@/hooks/useNotificationSound'
import { useMessageSync } from '@/hooks/useMessageSync'

interface RouteInfo {
  title: string
  subtitle?: string
  breadcrumbs: { label: string; path?: string }[]
}


export function AppLayout() {
  const { pathname } = useLocation()
  const { collapsed, toggle } = useSidebarCollapsed()
  // Mobile: breakpoint 768px — sidebar vira Drawer off-canvas
  const isMobile = useMediaQuery('(max-width: 768px)')
  const [drawerOpen, setDrawerOpen] = useState(false)
  // Fechar drawer automaticamente ao navegar
  useEffect(() => { setDrawerOpen(false) }, [pathname])
  // Toast in-app em tempo real (SSE) p/ alertas, conforme preferências do usuário.
  useInAppAlertToasts()
  // Som in-app (app aberto) ao chegar mensagem/alerta no chat — reusa a SSE única.
  useNotificationSound()
  // Sync on-device do store de mensagens (busca local): boot/delta por cursor +
  // fold SSE. Guardado por auth internamente; reusa a SSE única.
  useMessageSync()
  // Fonte de verdade unica: routeHeader deriva titulo, trilha e descricao do routeRegistry.
  // O mapa que existia aqui tinha 26 entradas para 98 rotas, e o fallback dele imprimia
  // pathname.slice(1) como se fosse rotulo — foi o que produziu "Home / admin/organizacoes".
  const cabecalho = routeHeader(pathname)
  // A tela pode dar o próprio título (a Entrada diz a pasta aberta) e dispensar a faixa.
  const tela = cabecalhoEfetivo(pathname, cabecalho, useTituloDaTelaStore((s) => s.atual))

  return (
    <>
      {/* Mobile: Drawer off-canvas (não ocupa espaço no flex row) */}
      {isMobile && (
        <Drawer
          opened={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          size={260}
          padding={0}
          withCloseButton={false}
          styles={{ body: { padding: 0, height: '100%' } }}
          zIndex={200}
        >
          <Sidebar collapsed={false} onToggle={() => setDrawerOpen(false)} />
        </Drawer>
      )}

      <Box style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
        {/* Desktop: sidebar inline no flex row */}
        {!isMobile && <Sidebar collapsed={collapsed} onToggle={toggle} />}

        <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          <Header
            title={tela.titulo}
            onToggleSidebar={isMobile ? () => setDrawerOpen((o) => !o) : toggle}
          />
          <PreviewBanner />
          <AcessoServidoresBanner />
          {pathname !== '/' && !pathname.startsWith('/dashboard') && tela.comFaixa && (
            <PageHeader
              title={cabecalho.titulo}
              subtitle={cabecalho.descricao}
              breadcrumbs={cabecalho.trilha}
              isMobile={isMobile}
            />
          )}
          <Box
            component="main"
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: isMobile ? '12px' : '20px 30px',
              backgroundColor: 'var(--ai-body-bg)',
            }}
          >
            {/* TASK-2473: ScreenErrorBoundary captura erros de render por tela.
                fallback="full" => exibe pagina dedicada com botao "Abrir chamado". */}
            <ScreenErrorBoundary fallback="full" screenName={cabecalho.titulo}>
              <Outlet />
            </ScreenErrorBoundary>
          </Box>
          {/* As janelas de terminal pinadas. Irma do `<Outlet/>` de proposito:
              elas precisam sobreviver a troca de rota, e o `<Outlet/>` e
              justamente o que troca. */}
          <TerminaisPinados />
          <AppFooter />
        </Box>
      </Box>
      {/* TASK-2473: FAB global "Reportar problema nesta tela" + Modal controlado por Zustand store.
          Ocultado no chat pois o compositor já ocupa a área de entrada. */}
      {!pathname.startsWith('/chat') && (
        <>
          <ReportScreenButton />
          <ReportScreenModal />
        </>
      )}
    </>
  )
}
